import { NavLink, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useData } from "../context/DataContext";
import { fmtDate } from "../lib/format";
import { pendingApprovalsCount } from "../lib/calc";
import DataUploadDialog from "./DataUploadDialog";
import BackupsDialog from "./BackupsDialog";
import ScenarioToggle from "./ScenarioToggle";
import PeriodSelector from "./PeriodSelector";
import InfoTip from "./InfoTip";
import ViewAsSelector from "./ViewAsSelector";
import { LogOut, Upload, RefreshCw, MoreVertical, Pencil, Archive } from "lucide-react";

const fmtImported = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const day = d.toLocaleDateString("en-NZ", { day: "2-digit", month: "short" });
  const time = d.toLocaleTimeString("en-NZ", { hour: "2-digit", minute: "2-digit" });
  return `${day} · ${time}`;
};

const NAV = [
  { to: "/admin/overview",    label: "Overview" },
  { to: "/admin/sheet",       label: "Sheet" },
  { to: "/admin/capacity",    label: "Capacity" },
  { to: "/admin/principals",  label: "Principals" },
  { to: "/admin/allocation",  label: "Allocation" },
  { to: "/admin/leave",       label: "Leave" },
  { to: "/admin/personnel",   label: "Personnel" },
  { to: "/admin/users",       label: "Users" },
  { to: "/admin/my-projects", label: "My Projects" },
  { to: "/admin/help",        label: "Manual" },
];

export default function Header() {
  const { logout, user } = useAuth();
  const { rawData, refresh, scenario, setScenario, monthsRange, setMonthsRange, viewAs } = useData();
  const data = rawData; // header chrome reads raw meta
  const navigate = useNavigate();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [backupsOpen, setBackupsOpen] = useState(false);

  const handleLogout = () => { logout(); navigate("/login"); };

  const allMonths = data?.meta?.months ?? [];
  const activeMonths = monthsRange?.length ? monthsRange : allMonths;
  const pendingApprovals = user?.role === "admin" ? pendingApprovalsCount(rawData) : 0;

  return (
    <header className="border-b border-[var(--rule-strong)] bg-white" data-testid="app-header">
      <div className="mx-auto max-w-[1480px] px-4 sm:px-6 lg:px-10">
        {/* Row 1: identity + actions */}
        <div className="flex items-center justify-between gap-4 pt-5 pb-4">
          <div className="min-w-0">
            <div className="eyebrow mb-1">Forecasting</div>
            <h1
              className="font-display tracked text-lg sm:text-xl md:text-2xl text-[var(--ink)] whitespace-nowrap"
              data-testid="brand-title"
            >
              MONK MACKENZIE
            </h1>
          </div>
          <div className="flex items-center gap-2 md:gap-3 shrink-0">
            <div className="text-right mr-1">
              <div className="eyebrow hidden sm:inline-flex items-center">
                Report date
                {data?.meta?.importedAt && (
                  <InfoTip side="bottom" label="Dataset" testid="imported-at">
                    Imported <strong className="font-mono-num">{fmtImported(data.meta.importedAt)}</strong>
                    {data?.meta?.importedBy && data.meta.importedBy !== "system_seed" && (
                      <> by {data.meta.importedBy}</>
                    )}
                    . Use <em>Re-feed</em> to replace the dataset with a fresh export.
                  </InfoTip>
                )}
              </div>
              <div
                className="font-mono-num text-sm text-[var(--ink)] whitespace-nowrap"
                data-testid="report-date"
              >
                {fmtDate(data?.meta?.reportDate)}
              </div>
            </div>
            {user?.role === "admin" && (
              <div className="hidden md:block"><ViewAsSelector /></div>
            )}
            <button
              onClick={() => setUploadOpen(true)}
              title="Replace the dataset with a fresh spreadsheet export"
              className="hidden md:inline-flex items-center gap-2 px-3 py-2 border border-[var(--rule-strong)] hover:bg-[var(--paper-soft)] font-display text-xs tracked-tight uppercase whitespace-nowrap"
              data-testid="open-upload-button"
            >
              <Upload size={12} /> Re-feed
            </button>
            <OverflowMenu
              isAdmin={user?.role === "admin"}
              onRefresh={refresh}
              onUpload={() => setUploadOpen(true)}
              onBackups={() => setBackupsOpen(true)}
              onLogout={handleLogout}
            />
          </div>
        </div>

        {/* Row 2: nav — horizontally scrollable when it doesn't fit */}
        <div className="relative">
          <nav className="flex gap-6 lg:gap-9 overflow-x-auto no-scrollbar whitespace-nowrap pr-8" data-testid="main-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={({ isActive }) => `nav-link shrink-0 inline-flex items-center gap-1.5 ${isActive ? "active" : ""}`}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
                {n.to === "/admin/capacity" && pendingApprovals > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 border border-[#8a6d1a]/50 bg-[#FBF6EA] text-[#7C3A06] font-mono-num text-[10px] leading-none rounded-full"
                    title={`${pendingApprovals} allocation${pendingApprovals === 1 ? "" : "s"} awaiting your approval`}
                    data-testid="capacity-pending-badge"
                  >
                    {pendingApprovals}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-10 bg-gradient-to-l from-white to-transparent xl:hidden" />
        </div>

        {/* Row 3: scenario + period — slim toolbar (global, applies to every view) */}
        {data && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2.5 border-t border-[var(--rule)]">
            <span className="eyebrow inline-flex items-center">
              Scenario
              <InfoTip side="bottom" label="What 'Scenario' means" testid="info-scenario">
                <strong>Confirmed</strong> — only projects with a signed contract, at full value.<br/>
                <strong>Forecast</strong> — confirmed plus tentative projects, at full value: the ceiling if everything currently proposed is won.
              </InfoTip>
            </span>
            <ScenarioToggle value={scenario} onChange={setScenario} />
            <div className="mx-1 h-4 w-px bg-[var(--rule)] hidden sm:block" />
            {/* Period selector applies exactly what was picked. The old
                `months.length === allMonths.length ? null : months` test threw
                the selection away whenever its month COUNT matched the
                stored-month count — with 12 stored months both "1 year" and
                "Last 12 months" silently fell back to the stored window (and
                so did any 12-month custom range), moving every figure on every
                page. */}
            <PeriodSelector
              allMonths={allMonths}
              value={activeMonths}
              onChange={setMonthsRange}
            />
            <InfoTip side="bottom" label="What 'Period' means">
              Narrows every view to a subset of months. Useful for looking at a quarter or half-year in isolation.
            </InfoTip>
            <span className="ml-auto eyebrow font-mono-num hidden sm:inline">
              Showing <span className="text-[var(--ink)]">{activeMonths.length}</span> of {allMonths.length} months
            </span>
          </div>
        )}
      </div>
      <DataUploadDialog open={uploadOpen} onClose={() => setUploadOpen(false)} />
      <BackupsDialog open={backupsOpen} onClose={() => setBackupsOpen(false)} />
      {user?.role === "admin" && viewAs && (
        <div className="bg-[#B45309] text-white px-6 lg:px-10 py-2.5 text-xs font-mono-num flex flex-wrap items-center justify-center gap-x-3 gap-y-1" data-testid="view-as-banner">
          <span className="font-semibold uppercase tracking-[0.08em] inline-flex items-center gap-1.5"><Pencil size={11} /> Acting as principal</span>
          <span className="hidden md:inline">·</span>
          <span>dataset is scoped to their portfolio · edits &amp; saves apply on their behalf. Click <span className="font-semibold">View as → Firm-wide</span> to exit.</span>
        </div>
      )}
    </header>
  );
}

// Secondary actions live behind a kebab menu; on narrow screens the primary
// actions (View as, Re-feed) fold in here too.
function OverflowMenu({ isAdmin, onRefresh, onUpload, onBackups, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        title="More actions"
        aria-label="More actions"
        className="p-2 border border-[var(--rule-strong)] hover:bg-[var(--paper-soft)]"
        data-testid="header-menu-button"
      >
        <MoreVertical size={14} />
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-2 w-56 bg-white border border-[var(--rule-strong)] shadow-sm animate-fade-up">
          {isAdmin && (
            <div className="md:hidden px-3 py-2.5 border-b border-[var(--rule)]">
              <div className="eyebrow mb-1.5">View as</div>
              <ViewAsSelector />
            </div>
          )}
          <MenuItem
            className="md:hidden"
            icon={Upload}
            label="Re-feed data"
            onClick={() => { onUpload(); setOpen(false); }}
            testid="menu-refeed"
          />
          {isAdmin && (
            <MenuItem
              icon={Archive}
              label="Backups"
              onClick={() => { onBackups(); setOpen(false); }}
              testid="menu-backups"
            />
          )}
          <MenuItem
            icon={RefreshCw}
            label="Refresh from dataset"
            onClick={() => { onRefresh(); setOpen(false); }}
            testid="refresh-button"
          />
          <MenuItem icon={LogOut} label="Sign out" onClick={onLogout} testid="logout-button" />
        </div>
      )}
    </div>
  );
}

function MenuItem({ icon: Icon, label, onClick, testid, className = "" }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-[var(--paper-soft)] inline-flex items-center gap-2.5 ${className}`}
      data-testid={testid}
    >
      <Icon size={13} className="text-[var(--muted-fg)]" />
      <span className="font-display tracked-tight uppercase text-xs">{label}</span>
    </button>
  );
}
